import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { APPLICATION_FORMS } from "@/lib/applicationForms";
import { BloomHerLogo } from "@/components/BloomHerLogo";
import { Loader2, Check, Heart } from "lucide-react";

export default function Apply() {
  const { type } = useParams();
  const form = APPLICATION_FORMS[type];
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!form) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-serif text-3xl text-wine">Application not found</h1>
          <Link to="/" className="mt-4 inline-block text-sm text-wine underline">Back home</Link>
        </div>
      </div>
    );
  }

  const setAns = (id, val) => setAnswers((p) => ({ ...p, [id]: val }));
  const toggleMulti = (id, opt) => setAnswers((p) => {
    const cur = p[id] || [];
    return { ...p, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const validate = () => {
    if (!contact.name.trim()) return "Please share your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) return "Please enter a valid email";
    for (const q of form.questions) {
      if (!q.required) continue;
      const v = answers[q.id];
      if (q.type === "checkbox") { if (!v || v.length === 0) return `Please answer: ${q.label}`; }
      else if (v === undefined || v === "" || v === null) return `Please answer: ${q.label}`;
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      // attach question labels for readable admin review
      const labeled = {};
      form.questions.forEach((q) => { labeled[q.label] = answers[q.id] ?? ""; });
      await api.post("/applications", { type: form.type, name: contact.name, email: contact.email, phone: contact.phone, answers: labeled });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  const field = "w-full rounded-xl border border-[#D8C2CD] bg-white px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine";

  return (
    <div className="min-h-screen bg-cream py-10 px-5" data-testid={`apply-page-${form.type}`}>
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-8"><Link to="/"><BloomHerLogo className="h-9" /></Link></div>

        {done ? (
          <div data-testid="apply-success" className="bg-white rounded-[2rem] border border-taupe p-10 text-center">
            <div className="h-16 w-16 rounded-full bg-wine/10 flex items-center justify-center mx-auto mb-5"><Heart className="h-7 w-7 text-wine" /></div>
            <h1 className="font-serif text-4xl text-wine">Your application is in</h1>
            <p className="text-[#6E5862] mt-3 max-w-md mx-auto">Thank you for sharing your heart with us. Our team will review your {form.title} application and reach out personally with the next step.</p>
            <Link to="/" className="mt-7 inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">Back to BloomHer</Link>
          </div>
        ) : (
          <div className="bg-white rounded-[2rem] border border-taupe p-7 sm:p-10">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Application</span>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-wine leading-tight">{form.title}</h1>
            <p className="mt-2 font-serif text-lg text-[#8B5A73]">{form.tagline}</p>
            <p className="mt-4 text-[#6E5862]">{form.intro}</p>

            <div className="mt-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="apply-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" className={field} />
                <input data-testid="apply-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" className={field} />
              </div>
              <input data-testid="apply-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone (optional)" className={field} />

              {form.questions.map((q, i) => (
                <div key={q.id} data-testid={`apply-q-${q.id}`} className="pt-3">
                  <label className="block font-medium text-[#3A1230] mb-2.5">{i + 1}. {q.label}{q.required && <span className="text-wine"> *</span>}</label>

                  {q.type === "textarea" && (
                    <textarea data-testid={`apply-input-${q.id}`} rows={3} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} placeholder={q.placeholder} className={`${field} resize-none`} />
                  )}

                  {q.type === "radio" && (
                    <div className="space-y-2">
                      {q.options.map((opt) => (
                        <button key={opt} type="button" data-testid={`apply-opt-${q.id}-${opt.slice(0, 8).replace(/\W/g, '')}`} onClick={() => setAns(q.id, opt)}
                          className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-all ${answers[q.id] === opt ? "border-wine bg-[#F7E9EF] text-wine font-medium" : "border-[#D8C2CD] bg-white text-[#6E5862] hover:bg-cream"}`}>
                          {answers[q.id] === opt && <Check className="inline h-4 w-4 mr-2" strokeWidth={3} />}{opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {q.type === "checkbox" && (
                    <div className="flex flex-wrap gap-2">
                      {q.options.map((opt) => {
                        const on = (answers[q.id] || []).includes(opt);
                        return (
                          <button key={opt} type="button" data-testid={`apply-opt-${q.id}-${opt.slice(0, 8).replace(/\W/g, '')}`} onClick={() => toggleMulti(q.id, opt)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-all ${on ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>
                            {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}{opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {q.type === "scale" && (
                    <div>
                      <div className="flex flex-wrap gap-1.5">
                        {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                          <button key={n} type="button" data-testid={`apply-scale-${q.id}-${n}`} onClick={() => setAns(q.id, n)}
                            className={`h-10 w-10 rounded-full text-sm font-medium transition-all ${answers[q.id] === n ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>{n}</button>
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-[#9B7E8D] mt-1.5 px-1"><span>{q.low}</span><span>{q.high}</span></div>
                    </div>
                  )}
                </div>
              ))}

              <button data-testid="apply-submit" onClick={submit} disabled={busy}
                className="w-full rounded-full bg-wine px-6 py-4 text-sm font-medium text-white hover:bg-wine-dark disabled:opacity-60 transition-all flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending your application</> : <>Submit my application</>}
              </button>
              <p className="text-center text-xs text-[#9B7E8D]">By applying you agree to be contacted by the BloomHer team. This is not medical advice.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
